import {
    PDFDocument,
    PDFName,
    PDFDict,
    PDFArray,
    PDFHexString,
    PDFString,
    PDFRawStream,
    PDFNumber,
    decodePDFRawStream
} from 'pdf-lib';
import { logger } from '../utils/logger';

const EMBEDDED_FILENAME = 'settings.xlsx';
const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
/** Catalog key: number of source pages before the markup snapshot page. */
const QD_BASE_PAGE_COUNT = PDFName.of('QDBasePageCount');

/** A4 in PDF points (72 dpi): width = short edge, height = long edge (portrait). */
const A4_WIDTH_PT = 595.28;
const A4_HEIGHT_PT = 841.89;
/** Minimal margin so the snapshot uses almost the full sheet (printers often crop ~3 mm). */
const A4_SNAPSHOT_MARGIN_PT = 10;

function readStoredBasePageCount(catalog: PDFDict): number | null {
    if (!catalog.has(QD_BASE_PAGE_COUNT)) {
        return null;
    }
    const value = catalog.lookup(QD_BASE_PAGE_COUNT);
    if (value instanceof PDFNumber) {
        const n = value.asNumber();
        return Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
    }
    return null;
}

/**
 * How many pages belong to the original drawing (everything after that is replaced on save).
 */
function resolveBasePageCount(source: PDFDocument): number {
    const pageCount = source.getPageCount();
    const stored = readStoredBasePageCount(source.catalog as PDFDict);
    if (stored != null) {
        return Math.min(stored, pageCount);
    }
    const hasEmbedded = extractRawAttachments(source).length > 0;
    if (hasEmbedded && pageCount > 1) {
        return 1;
    }
    return pageCount;
}

/** Attach Excel bytes to any PDF blob (e.g. jsPDF-generated report). */
export async function embedExcelInPdfBlob(
    pdfBlob: Blob,
    excelBytes: Uint8Array,
    markupSnapshotPng?: Uint8Array | null
): Promise<Blob> {
    const pdfBytes = new Uint8Array(await pdfBlob.arrayBuffer());
    const source = await PDFDocument.load(pdfBytes);
    const basePageCount = resolveBasePageCount(source);
    const pageCount = source.getPageCount();
    const copyCount = Math.min(basePageCount, pageCount);

    const pdfDoc = await PDFDocument.create();
    if (copyCount > 0) {
        const indices = Array.from({ length: copyCount }, (_, i) => i);
        const copied = await pdfDoc.copyPages(source, indices);
        copied.forEach((page) => pdfDoc.addPage(page));
    }
    (pdfDoc.catalog as PDFDict).set(QD_BASE_PAGE_COUNT, PDFNumber.of(basePageCount));

    pdfDoc.attach(excelBytes, EMBEDDED_FILENAME, { mimeType: EXCEL_MIME });
    if (markupSnapshotPng && markupSnapshotPng.length > 0) {
        try {
            const pngImage = await pdfDoc.embedPng(markupSnapshotPng);
            const iw = pngImage.width;
            const ih = pngImage.height;
            const pageW = iw > ih ? A4_HEIGHT_PT : A4_WIDTH_PT;
            const pageH = iw > ih ? A4_WIDTH_PT : A4_HEIGHT_PT;
            const page = pdfDoc.addPage([ pageW, pageH ]);
            const m = A4_SNAPSHOT_MARGIN_PT;
            const innerW = pageW - 2 * m;
            const innerH = pageH - 2 * m;
            const scale = Math.min(innerW / iw, innerH / ih);
            const dw = iw * scale;
            const dh = ih * scale;
            const x = m + (innerW - dw) / 2;
            const yBottom = m + (innerH - dh) / 2;
            page.drawImage(pngImage, { x, y: yBottom, width: dw, height: dh });
        } catch (error) {
            logger.error('[pdfAttachmentService] Failed to append markup snapshot page:', error);
        }
    }
    const savedPdf = await pdfDoc.save();
    return new Blob([ savedPdf ], { type: 'application/pdf' });
}

export async function embedExcelInPdf(
    pdfFile: File,
    excelBytes: Uint8Array,
    markupSnapshotPng?: Uint8Array | null
): Promise<Blob> {
    return embedExcelInPdfBlob(pdfFile, excelBytes, markupSnapshotPng);
}

function extractRawAttachments(pdfDoc: PDFDocument): { fileName: PDFHexString | PDFString; fileSpec: PDFDict }[] {
    const catalog = pdfDoc.catalog as PDFDict;
    if (!catalog.has(PDFName.of('Names'))) return [];
    const Names = catalog.lookup(PDFName.of('Names'), PDFDict);
    if (!Names.has(PDFName.of('EmbeddedFiles'))) return [];
    let EmbeddedFiles = Names.lookup(PDFName.of('EmbeddedFiles'), PDFDict);
    if (!EmbeddedFiles.has(PDFName.of('Names')) && EmbeddedFiles.has(PDFName.of('Kids'))) {
        EmbeddedFiles = EmbeddedFiles.lookup(PDFName.of('Kids'), PDFArray).lookup(0, PDFDict);
    }
    if (!EmbeddedFiles.has(PDFName.of('Names'))) return [];
    const EFNames = EmbeddedFiles.lookup(PDFName.of('Names'), PDFArray);
    const rawAttachments: { fileName: PDFHexString | PDFString; fileSpec: PDFDict }[] = [];
    for (let idx = 0; idx < EFNames.size(); idx += 2) {
        const fileName = EFNames.lookup(idx) as PDFHexString | PDFString;
        const fileSpec = EFNames.lookup(idx + 1, PDFDict);
        rawAttachments.push({ fileName, fileSpec });
    }
    return rawAttachments;
}

export async function extractExcelFromPdf(pdfFile: File): Promise<File | null> {
    try {
        const pdfBytes = new Uint8Array(await pdfFile.arrayBuffer());
        const pdfDoc = await PDFDocument.load(pdfBytes);
        const rawAttachments = extractRawAttachments(pdfDoc);

        let lastMatch: File | null = null;
        for (const { fileName, fileSpec } of rawAttachments) {
            if (!fileSpec.has(PDFName.of('EF'))) continue;
            const EF = fileSpec.lookup(PDFName.of('EF'), PDFDict);
            const stream = EF.lookup(PDFName.of('F'), PDFRawStream);
            const name = (fileName as { decodeText?: () => string }).decodeText?.() || '';
            if (name.toLowerCase().endsWith('.xlsx') || name.toLowerCase().endsWith('.xls')) {
                const decoded = decodePDFRawStream(stream);
                const data = decoded.getBytes();
                if (data && data.length > 0) {
                    lastMatch = new File([ data ], name || EMBEDDED_FILENAME, { type: EXCEL_MIME });
                }
            }
        }
        return lastMatch;
    } catch (error) {
        logger.error('[pdfAttachmentService] Failed to extract Excel from PDF:', error);
        return null;
    }
}
